import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '../lib/useIsMobile';
import {
  JSNS,
  type Conversation,
  type EmailAddr,
  type Folder,
  type Message,
  type MimePart,
  type Mountpoint,
} from '@zimbra-webif/sdk';
import {
  useConversation,
  useConversations,
  useFolders,
  useMailAction,
  useMessage,
  useMessages,
  useSearch,
} from '../lib/queries';
import { getLoadExternalImages } from '../lib/prefs';
import { useClient } from '../lib/ClientContext';
import { useCreateContact } from '../lib/contacts-queries';
import { threadPeople, type ThreadMsg, type ThreadPeople } from '../lib/threadPeople';
import { imgNeedsLightPlate } from '../lib/logoPlate';
import { fmtDay, fmtDayTime, fmtSize } from '../lib/format';
import { hitFolder, MAIL_DRAG_TYPE, type DraggedMail } from '../lib/clean';
import { canMouseDrag, touchDragStart, TOUCH_DRAGGING } from '../lib/touchDrag';
import { ComposeWindow, type ComposeInit } from './ComposeWindow';
import { useCleanRuleFromMail } from './CleanPanel';
import {
  IconBroom,
  IconCalendar,
  IconFile,
  IconFlag,
  IconForward,
  IconImage,
  IconJunk,
  IconMail,
  IconPaperclip,
  IconReply,
  IconReplyAll,
  IconShare,
  IconTrash,
} from './icons';

const SORT_OPTIONS: Array<{ v: string; label: string }> = [
  { v: 'dateDesc', label: 'Date ↓' },
  { v: 'dateAsc', label: 'Date ↑' },
  { v: 'subjAsc', label: 'Subject' },
  { v: 'nameAsc', label: 'Sender' },
  { v: 'sizeDesc', label: 'Size' },
  { v: 'readDesc', label: 'Unread first' },
];

interface FolderRow {
  id: string;
  name: string;
  depth: number;
  unread: number;
  shared: boolean;
}

const NONMAIL_VIEWS = new Set(['appointment', 'task', 'contact', 'document', 'wiki', 'chat', 'voice']);
const HIDE_NAMES = new Set(['Chats', 'Files shared with me']);

function isMailFolder(view: string | undefined, name: string | undefined): boolean {
  if (view && NONMAIL_VIEWS.has(view)) return false;
  if (name && HIDE_NAMES.has(name)) return false;
  return true;
}

function buildRows(root: Folder | undefined): FolderRow[] {
  const rows: FolderRow[] = [];
  const walk = (folders: Folder[] | undefined, links: Mountpoint[] | undefined, depth: number) => {
    for (const f of folders ?? []) {
      if (!isMailFolder(f.view, f.name)) continue;
      rows.push({ id: f.id, name: f.name ?? '', depth, unread: f.u ?? 0, shared: false });
      walk(f.folder, f.link, depth + 1);
    }
    for (const l of links ?? []) {
      if (!isMailFolder(l.view, l.name)) continue;
      rows.push({ id: l.id, name: l.name ?? '', depth, unread: l.u ?? 0, shared: true });
      walk(l.folder, l.link, depth + 1);
    }
  };
  walk(root?.folder, root?.link, 0);
  return rows;
}

function fromAddr(m: Message): EmailAddr | undefined {
  return m.e?.find((e) => e.t === 'f') ?? m.e?.[0];
}
function senderOf(m: Message): string {
  const a = fromAddr(m);
  return a?.p ?? a?.d ?? a?.a ?? '(unknown)';
}
/** "Name <email>" (or just the email) for one recipient type: t=to, c=cc. */
function addrsByType(m: Message, t: string): string[] {
  return (m.e ?? [])
    .filter((e) => e.t === t)
    .map((e) => {
      const name = e.p ?? e.d ?? '';
      const addr = e.a ?? '';
      if (name && addr && name !== addr) return `${name} <${addr}>`;
      return addr || name;
    })
    .filter(Boolean);
}
function addrObjsByType(m: Message, t: string): EmailAddr[] {
  return (m.e ?? []).filter((e) => e.t === t && (e.a || e.p || e.d));
}
/** "Name <addr>" (or just the address) for an EmailAddr. */
function addrFull(e: EmailAddr): string {
  const name = e.p ?? e.d ?? '';
  const addr = e.a ?? '';
  return name && addr && name !== addr ? `${name} <${addr}>` : addr || name;
}
/** Zimbra system folder ids. */
const JUNK_FOLDER_ID = '4';
function participantsOf(c: Conversation): string {
  const names = (c.e ?? []).map((e) => e.p ?? e.d ?? e.a ?? '').filter(Boolean);
  return names.length ? names.join(', ') : '(unknown)';
}
function initials(name: string): string {
  const parts = name.replace(/[<>"]/g, '').trim().split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}
/** Validated sender email address, or null. */
function validEmail(addr?: string): string | null {
  const a = (addr ?? '').trim().toLowerCase();
  return /^[^@\s]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(a) ? a : null;
}
function msgEmail(m: Message): string | null {
  return validEmail(fromAddr(m)?.a);
}
function convEmail(c: Conversation): string | null {
  const e = (c.e ?? []).find((x) => x.t === 'f') ?? (c.e ?? [])[0];
  return validEmail(e?.a);
}
/**
 * What the Clean inbox panel needs from a mail to suggest a rule. `viewed` is
 * the folder on screen (exact, also for a share); in search results the hit's
 * own folder is used instead.
 */
function mailOfMsg(m: Message, viewed: string | undefined): DraggedMail | null {
  const a = msgEmail(m);
  return a ? { from: a, name: fromAddr(m)?.p ?? fromAddr(m)?.d, subject: m.su, folderId: viewed ?? hitFolder(m.id, m.l) } : null;
}
function mailOfConv(c: Conversation, viewed: string | undefined): DraggedMail | null {
  const a = convEmail(c);
  const f = (c.e ?? []).find((x) => x.t === 'f');
  const m0 = c.m?.[0];
  return a ? { from: a, name: f?.p ?? f?.d, subject: c.su, folderId: viewed ?? (m0 ? hitFolder(m0.id, m0.l) : undefined) } : null;
}
/** Mouse/trackpad: a list row can be dragged onto the Clean inbox panel to suggest a rule (touch: see touchDrag). */
function startMailDrag(ev: React.DragEvent, d: DraggedMail | null) {
  if (!d) return ev.preventDefault();
  ev.dataTransfer.setData(MAIL_DRAG_TYPE, JSON.stringify(d));
  ev.dataTransfer.effectAllowed = 'copy';
}

/**
 * Batched badge resolution. Avatars register their sender email; the provider
 * collects the set and asks the BFF ONCE (`/api/badge/peek`) for all of them,
 * instead of one request per row. Re-checks lightly (one request / 20s) only
 * while some senders are still being warmed.
 */
interface BadgeInfo {
  ok: boolean;
  kind: string;
  pending: boolean;
  /** badge version (record timestamp) — part of the image URL so a changed badge is re-fetched */
  v?: number;
  /** why this picture (mo2-ai evidence label, e.g. "matched by handle") and the page it came from */
  why?: string;
  page?: string;
  /** "Keep this picture" is on */
  pinned?: boolean;
}

/** "Jane Doe — matched by handle (linkedin.com)" for the avatar tooltip. */
function badgeTooltip(name: string, b: BadgeInfo | undefined): string {
  if (!b?.ok || !b.why) return name;
  let host = '';
  try {
    host = b.page ? new URL(b.page).hostname.replace(/^www\./, '') : '';
  } catch {
    /* not a URL */
  }
  return `${name} — ${b.why}${host ? ` (${host})` : ''}${b.pinned ? ' · kept' : ''}`;
}
const BadgeCtx = createContext<{ register: (email: string, name?: string) => void; map: Record<string, BadgeInfo> }>({
  register: () => {},
  map: {},
});

function BadgeProvider({ children }: { children: ReactNode }) {
  const { session } = useClient();
  const seen = useRef<Set<string>>(new Set());
  const names = useRef<Record<string, string>>({});
  const flush = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const register = useCallback((email: string, name?: string) => {
    if (email && name && !names.current[email] && !name.includes(',') && name !== email) names.current[email] = name;
    if (!email || seen.current.has(email)) return;
    seen.current.add(email);
    if (flush.current) clearTimeout(flush.current);
    flush.current = setTimeout(() => setEmails([...seen.current].sort()), 60);
  }, []);
  const q = useQuery({
    queryKey: ['badges', emails],
    enabled: emails.length > 0,
    staleTime: 60_000,
    refetchInterval: (query) =>
      Object.values((query.state.data ?? {}) as Record<string, BadgeInfo>).some((v) => v.pending) ? 20_000 : false,
    queryFn: async (): Promise<Record<string, BadgeInfo>> => {
      // POST: carries the sender names too (person searches need them) without a huge URL
      const n: Record<string, string> = {};
      for (const e of emails) if (names.current[e]) n[e] = names.current[e]!;
      const res = await fetch('/api/badge/peek', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'x-zwf-csrf': session.csrfToken },
        body: JSON.stringify({ e: emails, n }),
      });
      return res.ok ? ((await res.json()) as Record<string, BadgeInfo>) : {};
    },
  });
  return <BadgeCtx.Provider value={{ register, map: q.data ?? {} }}>{children}</BadgeCtx.Provider>;
}

function useBadgeFor(email: string | null, name?: string): BadgeInfo | undefined {
  const { register, map } = useContext(BadgeCtx);
  useEffect(() => {
    if (email) register(email, name);
  }, [email, name, register]);
  return email ? map[email] : undefined;
}

/**
 * Dark-glass-orb sender avatar. Initials by default; shows the sender's badge
 * (person photo or company logo, from the batched map) when resolved. The image
 * is only requested once the badge is known to exist (no wasted 204 requests).
 */
function Avatar({ name, email, large, mini }: { name: string; email: string | null; large?: boolean; mini?: boolean }) {
  const badge = useBadgeFor(email, name);
  const [imgState, setImgState] = useState<'pending' | 'ok' | 'fail'>('pending');
  const [plate, setPlate] = useState(false);
  const kind = badge?.kind === 'person_photo' ? 'person_photo' : 'company_logo';
  const showImg = !!email && badge?.ok === true && imgState !== 'fail';
  const loaded = showImg && imgState === 'ok';
  return (
    <div
      className={`avatar${large ? ' lg' : ''}${mini ? ' mini' : ''}${loaded ? ' has-logo' : ''}`}
      title={loaded ? badgeTooltip(name, badge) : mini ? name : undefined}
    >
      {loaded ? null : <span className="avatar-initials">{initials(name)}</span>}
      {showImg ? (
        <img
          className={`avatar-logo kind-${kind}${plate ? ' plate' : ''}`}
          src={`/api/badge?email=${encodeURIComponent(email as string)}${badge?.v ? `&v=${badge.v}` : ''}`}
          alt=""
          loading="lazy"
          onLoad={(e) => {
            setPlate(kind === 'company_logo' && imgNeedsLightPlate(e.currentTarget));
            setImgState('ok');
          }}
          onError={() => setImgState('fail')}
        />
      ) : null}
    </div>
  );
}
/**
 * Starter + other writers for the visible thread rows, in ONE BatchRequest of
 * SearchConv calls per page (senders only, newest first). Keyed by each thread's
 * newest message, so a thread is looked up again only when a new mail arrives.
 * A thread that fails (e.g. some shares) just keeps today's single icon.
 */
function useThreadPeople(convs: Conversation[], self: string): Map<string, ThreadPeople> {
  const { client } = useClient();
  const keys = convs.filter((c) => (c.n ?? 1) > 1).map((c) => `${c.id}~${c.m?.[0]?.id ?? ''}`);
  const q = useQuery({
    queryKey: ['thread-people', self, keys],
    enabled: keys.length > 0,
    staleTime: Infinity,
    queryFn: async (): Promise<Array<[string, ThreadPeople]>> => {
      const out: Array<[string, ThreadPeople]> = [];
      for (let i = 0; i < keys.length; i += 25) {
        const chunk = keys.slice(i, i + 25).map((k) => k.split('~')[0]!);
        const res = (await client.batch(
          chunk.map((cid) => ({
            jsns: JSNS.mail,
            command: 'SearchConv',
            params: { cid, query: 'is:anywhere', fetch: 'none', sortBy: 'dateDesc', limit: 100 },
          })),
        )) as { SearchConvResponse?: unknown };
        const list = ([] as Array<{ requestId?: string; m?: Array<{ f?: string; e?: Array<ThreadMsg & { t?: string }> }> }>).concat(
          (res.SearchConvResponse as never) ?? [],
        );
        for (const r of list) {
          const cid = chunk[Number(r.requestId)];
          if (!cid) continue;
          const msgs: ThreadMsg[] = (r.m ?? []).map((m) => {
            const f = m.e?.find((e) => e.t === 'f');
            return { a: f?.a, p: f?.p, d: f?.d, f: m.f };
          });
          const tp = threadPeople(msgs, self);
          if (tp) out.push([cid, tp]);
        }
      }
      return out;
    },
  });
  return useMemo(() => new Map(q.data ?? []), [q.data]);
}

function msgUnread(m: Message): boolean {
  return typeof m.f === 'string' && m.f.includes('u');
}
function convUnread(c: Conversation): boolean {
  return (c.u ?? 0) > 0;
}
// Zimbra flag 'a' = the item has an attachment (verified against live mail).
function msgHasAttach(m: Message): boolean {
  return (m.f ?? '').includes('a');
}
function convHasAttach(c: Conversation): boolean {
  return (c.f ?? '').includes('a');
}
function fmtDate(ms?: number): string {
  return fmtDay(ms);
}

/** Coarse label for the floating scroll-date indicator. */
function floatLabel(ms?: number): string {
  return ms
    ? new Date(ms).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
}

/** SpamAssassin score from a message's requested headers (X-Spam-Score / Status). */
function parseSpamScore(m: Message): number | null {
  const a = (m as { _attrs?: Record<string, string> })._attrs;
  if (!a) return null;
  const raw = a['X-Spam-Score'];
  if (raw && raw.trim() !== '') {
    const n = parseFloat(raw);
    if (!Number.isNaN(n)) return n;
  }
  const status = a['X-Spam-Status'];
  if (status) {
    const m2 = /score=(-?\d+(?:\.\d+)?)/.exec(status);
    if (m2 && m2[1] !== undefined) return parseFloat(m2[1]);
  }
  return null;
}

function spamZone(score: number): { label: string; color: string } {
  if (score >= 5) return { label: 'Spam', color: '#e0314f' };
  if (score >= 3) return { label: 'Likely spam', color: '#ff8c1a' };
  if (score >= 1) return { label: 'Suspicious', color: '#f6c500' };
  return { label: 'Safe', color: '#28a745' };
}

/** Speedometer-style spam gauge (safe → spam) for a message's score. */
function SpamMeter({ score }: { score: number }) {
  const MIN = -3;
  const MAX = 8;
  const t = Math.max(0, Math.min(1, (score - MIN) / (MAX - MIN)));
  const deg = -90 + t * 180;
  const z = spamZone(score);
  return (
    <div className="spam-meter" title={`Spam score ${score.toFixed(1)} — ${z.label}`}>
      <svg viewBox="0 0 80 46" className="spam-gauge" aria-hidden="true">
        <defs>
          <linearGradient id="spamgrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#28a745" />
            <stop offset="45%" stopColor="#f6c500" />
            <stop offset="75%" stopColor="#ff8c1a" />
            <stop offset="100%" stopColor="#e0314f" />
          </linearGradient>
        </defs>
        <path d="M8 40 A 32 32 0 0 1 72 40" fill="none" stroke="url(#spamgrad)" strokeWidth="7" strokeLinecap="round" />
        <line x1="40" y1="40" x2="40" y2="13" stroke="var(--text)" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${deg} 40 40)`} />
        <circle cx="40" cy="40" r="3.5" fill="var(--text)" />
      </svg>
      <span className="spam-meter-label" style={{ color: z.color }}>
        {z.label} · {score.toFixed(1)}
      </span>
    </div>
  );
}

/** Sort a thread's messages by the currently-selected list sort rule. */
function sortMessages(msgs: Message[], sortBy: string): Message[] {
  const out = [...msgs];
  switch (sortBy) {
    case 'dateAsc':
      return out.sort((a, b) => (a.d ?? 0) - (b.d ?? 0));
    case 'subjAsc':
      return out.sort((a, b) => (a.su ?? '').localeCompare(b.su ?? ''));
    case 'nameAsc':
      return out.sort((a, b) => senderOf(a).localeCompare(senderOf(b)));
    case 'sizeDesc':
      return out.sort((a, b) => (b.s ?? 0) - (a.s ?? 0));
    case 'readDesc':
      return out.sort((a, b) => Number(msgUnread(b)) - Number(msgUnread(a)) || (b.d ?? 0) - (a.d ?? 0));
    case 'dateDesc':
    default:
      return out.sort((a, b) => (b.d ?? 0) - (a.d ?? 0));
  }
}

function extractBody(parts: MimePart[] | undefined): { html?: string; text?: string } {
  const acc: { html?: string; text?: string } = {};
  const visit = (mp: MimePart[] | undefined) => {
    for (const p of mp ?? []) {
      if (p.ct === 'text/html' && typeof p.content === 'string') acc.html ??= p.content;
      if (p.ct === 'text/plain' && typeof p.content === 'string') acc.text ??= p.content;
      visit(p.mp);
    }
  };
  visit(parts);
  return acc;
}
function readableHtml(html: string, loadImages: boolean): string {
  // External images load by default; when disabled in config, a CSP blocks
  // remote image fetches (only self/data/cid allowed) so nothing leaks.
  // loadImages: upgrade http→https so remote images aren't mixed-content-blocked
  // on the https site. Off: CSP blocks remote image fetches entirely.
  const csp = loadImages
    ? `<meta http-equiv="Content-Security-Policy" content="upgrade-insecure-requests">`
    : `<meta http-equiv="Content-Security-Policy" content="img-src 'self' data: cid:;">`;
  // base target=_blank so links open in a new tab (the iframe allows popups).
  return `<!doctype html><meta charset="utf-8"><base target="_blank">${csp}<style>
    html{color-scheme:light}
    body{margin:0;padding:16px 20px;color:#1d1d1f;background:#fff;
      font:15px/1.62 -apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
      word-wrap:break-word;overflow-wrap:anywhere}
    img{max-width:100%;height:auto}a{color:#0a66ff}
    blockquote{border-left:3px solid #d2d2d7;margin:0 0 0 2px;padding-left:12px;color:#555}
    pre{white-space:pre-wrap}
  </style>${html}`;
}
/** Map a message's inline parts: content-id (no <>) → part number. */
function buildCidMap(parts: MimePart[] | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  const visit = (mp: MimePart[] | undefined) => {
    for (const p of mp ?? []) {
      if (p.ci && p.part) map[p.ci.replace(/[<>]/g, '')] = p.part;
      visit(p.mp);
    }
  };
  visit(parts);
  return map;
}
function escapeBodyText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** Decode HTML entities that Outlook/Exchange put in cid refs (e.g. &#64; → @). */
function decodeEntities(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_m, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_m, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&amp;/gi, '&');
}
/** Rewrite `cid:<id>` refs (HTML src=) to the same-origin /api/content image. */
function rewriteCid(html: string, msgId: string, map: Record<string, string>): string {
  return html.replace(/cid:([^"'>)\s]+)/gi, (whole, id: string) => {
    const part = map[decodeEntities(id).replace(/[<>]/g, '')];
    return part ? `/api/content?id=${encodeURIComponent(msgId)}&part=${encodeURIComponent(part)}&disp=i` : whole;
  });
}
/**
 * Marketing mail (AliExpress, etc.) lazy-loads images with JS: the real URL sits
 * in `data-src`/`data-original` and `src` is a blank/placeholder, swapped on load.
 * We block scripts for safety, so the swap never runs and images stay blank — so
 * promote the data-* URL into `src` (and data-srcset → srcset) ourselves.
 */
function promoteLazyImages(html: string): string {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const m = /\sdata-(?:src|original|lazy-src|echo|src-large)\s*=\s*["']([^"']+)["']/i.exec(tag);
    if (!m?.[1]) return tag;
    const url = m[1];
    let next = /\ssrc\s*=\s*["'][^"']*["']/i.test(tag)
      ? tag.replace(/\ssrc\s*=\s*["'][^"']*["']/i, ` src="${url}"`)
      : tag.replace(/<img\b/i, `<img src="${url}"`);
    const ss = /\sdata-srcset\s*=\s*["']([^"']+)["']/i.exec(tag);
    if (ss?.[1]) {
      next = /\ssrcset\s*=\s*["'][^"']*["']/i.test(next)
        ? next.replace(/\ssrcset\s*=\s*["'][^"']*["']/i, ` srcset="${ss[1]}"`)
        : next.replace(/<img\b/i, `<img srcset="${ss[1]}"`);
    }
    return next;
  });
}
/** Inline (embedded) image parts of a message — the ones meant to show in the body. */
function inlineImages(parts: MimePart[] | undefined): MimePart[] {
  const out: MimePart[] = [];
  const visit = (mp: MimePart[] | undefined) => {
    for (const p of mp ?? []) {
      if (p.part && (p.ct ?? '').startsWith('image/') && (p.cd === 'inline' || !!p.ci)) out.push(p);
      visit(p.mp);
    }
  };
  visit(parts);
  return out;
}
/**
 * Final HTML to render in the reading iframe, or null → render as plain text.
 * Inline images render from HTML (src="cid:…") or plain-text `[cid:…]` placeholders;
 * any embedded image NOT referenced in the body is appended so pics always show.
 */
function messageBodyHtml(m: Message, body: { html?: string; text?: string }, loadImages: boolean): string | null {
  const map = buildCidMap(m.mp);
  const embedded = inlineImages(m.mp);
  let out: string | null = null;
  if (body.html && body.html.trim()) {
    out = rewriteCid(body.html, m.id, map);
  } else if (body.text && (/\[cid:[^\]]+\]/i.test(body.text) || embedded.length > 0)) {
    out = `<pre style="white-space:pre-wrap;font:inherit;margin:0">${escapeBodyText(body.text).replace(
      /\[cid:([^\]]+)\]/gi,
      (_w, id: string) => {
        const part = map[id.replace(/[<>]/g, '')];
        return part ? `<img src="/api/content?id=${encodeURIComponent(m.id)}&part=${encodeURIComponent(part)}&disp=i">` : '';
      },
    )}</pre>`;
  }
  if (out === null) return null;
  // append any embedded image not already shown in the body
  const extras = embedded.filter((p) => !out!.includes(`part=${encodeURIComponent(p.part as string)}&disp=i`));
  if (extras.length > 0) {
    out +=
      `<div style="margin-top:14px;display:flex;flex-wrap:wrap;gap:10px">` +
      extras
        .map(
          (p) =>
            `<img src="/api/content?id=${encodeURIComponent(m.id)}&part=${encodeURIComponent(p.part as string)}&disp=i" style="max-width:100%;height:auto;border-radius:6px">`,
        )
        .join('') +
      `</div>`;
  }
  // when images are allowed, promote JS-lazy-loaded images (data-src) so they show
  if (loadImages) out = promoteLazyImages(out);
  return readableHtml(out, loadImages);
}
function plainBody(m: Message): string {
  const b = extractBody(m.mp);
  if (b.text) return b.text;
  if (b.html) return b.html.replace(/<[^>]+>/g, '').replace(/\s+\n/g, '\n').trim();
  return '';
}

interface AttachmentRow {
  part: string;
  filename: string;
  size?: number;
  ct?: string;
}
function attachmentsOf(m: Message): AttachmentRow[] {
  const out: AttachmentRow[] = [];
  const visit = (mp: MimePart[] | undefined) => {
    for (const p of mp ?? []) {
      // calendar invites (.ics) are surfaced as an "Add to calendar" card, not a file
      const isCal = p.ct === 'text/calendar' || (p.ct ?? '').startsWith('application/ics') || /\.ics$/i.test(p.filename ?? '');
      // Any named part is an attachment — including cd='inline' images (Outlook
      // embeds), which are flagged as attachments but were previously hidden.
      const isAttach = !isCal && (p.cd === 'attachment' || (!!p.filename && !p.body));
      if (isAttach && p.filename && p.part) out.push({ part: p.part, filename: p.filename, size: p.s, ct: p.ct });
      visit(p.mp);
    }
  };
  visit(m.mp);
  return out;
}
/** image/* and PDF open inline (preview); everything else downloads. */
function attachInline(ct?: string): boolean {
  return !!ct && (ct.startsWith('image/') || ct === 'application/pdf');
}
/** Short type label for the circular attachment badge (PDF, JPG, DXF…). */
function attType(a: AttachmentRow): string {
  const dot = a.filename.lastIndexOf('.');
  const ext = dot >= 0 ? a.filename.slice(dot + 1) : '';
  if (ext && ext.length <= 4) return ext.toUpperCase();
  const sub = (a.ct ?? '').split('/')[1] ?? (a.ct ?? '').split('/')[0] ?? '';
  return (sub.replace(/[^a-z0-9]/gi, '').slice(0, 4) || 'FILE').toUpperCase();
}
/** A meaningful glyph for the attachment type (image vs document/file). */
function attIcon(a: AttachmentRow): typeof IconFile {
  const t = attType(a);
  const img = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'HEIC', 'SVG', 'BMP', 'TIFF'];
  if (img.includes(t) || (a.ct ?? '').startsWith('image/')) return IconImage;
  return IconFile;
}
function attColor(t: string): string {
  if (t === 'PDF') return 'linear-gradient(160deg, #ff5a6e, #d4143a)';
  if (['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'HEIC', 'SVG', 'BMP'].includes(t)) return 'linear-gradient(160deg, #2ec5a8, #0a8f74)';
  if (['DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX', 'CSV'].includes(t)) return 'linear-gradient(160deg, #5b8cff, #2f5fe0)';
  if (['ZIP', 'RAR', '7Z', 'GZ', 'TAR'].includes(t)) return 'linear-gradient(160deg, #c79a3a, #8a6a14)';
  return 'linear-gradient(160deg, #3a4252, #1c2330)';
}
function Attachments({ msgId, parts }: { msgId: string; parts: AttachmentRow[] }) {
  if (parts.length === 0) return null;
  return (
    <div className="attachments">
      {parts.map((a) => {
        const disp = attachInline(a.ct) ? 'i' : 'a';
        return (
          <a
            key={a.part}
            className="att"
            href={`/api/content?id=${encodeURIComponent(msgId)}&part=${encodeURIComponent(a.part)}&disp=${disp}`}
            target="_blank"
            rel="noreferrer"
            {...(disp === 'a' ? { download: a.filename } : {})}
          >
            📎 {a.filename}
            {a.size ? <span className="att-size"> · {fmtSize(a.size)}</span> : null}
          </a>
        );
      })}
    </div>
  );
}

/**
 * Sanitize email HTML for safe insertion into the compose contentEditable:
 * drop scripts/styles/iframes/images/etc and all event handlers + javascript:
 * URLs, but keep text formatting (tables, fonts, colours, links).
 */
function sanitizeQuoteHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,iframe,object,embed,link,meta,base,title,img,svg,form,input,button').forEach((e) => e.remove());
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of [...el.attributes]) {
      const n = attr.name.toLowerCase();
      if (n.startsWith('on') || n === 'srcset' || (n === 'href' && /^\s*javascript:/i.test(attr.value)) || (n === 'src' && /^\s*javascript:/i.test(attr.value))) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}
/** The original message as clean quoted HTML (formatted, sanitized). */
function quotedOriginal(m: Message): string {
  const b = extractBody(m.mp);
  const inner =
    b.html && b.html.trim()
      ? sanitizeQuoteHtml(b.html)
      : escapeBodyText(b.text ?? '').replace(/\n/g, '<br>');
  return `<blockquote style="margin:0 0 0 4px;padding-left:12px;border-left:3px solid #c7ccd6;color:#444">${inner}</blockquote>`;
}
/** A message is a saved draft (Zimbra flag 'd'). */
function isDraft(m: { f?: string }): boolean {
  return (m.f ?? '').includes('d');
}
/**
 * Reopen a saved draft in the compose window exactly as it was left: recipients, subject,
 * body as saved (its signature is already in it), its own attachments (kept, by message id +
 * part — never re-uploaded) and the reply/forward link. Save overwrites it, Send removes it.
 */
function draftInit(m: Message): ComposeInit {
  const addrs = (t: string) =>
    (m.e ?? [])
      .filter((e) => e.t === t && e.a)
      .map((e) => e.a)
      .join(', ');
  const b = extractBody(m.mp);
  let html = b.html && b.html.trim() ? b.html : escapeBodyText(b.text ?? '').replace(/\n/g, '<br>');
  // only the body's content goes into the editor (no <html>/<head>, no scripts or page styles)
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, link, meta, title').forEach((n) => n.remove());
  html = doc.body.innerHTML;
  const origid = typeof m.origid === 'string' ? m.origid : undefined;
  const rt = m.rt === 'r' || m.rt === 'w' ? m.rt : undefined;
  return {
    title: 'Draft',
    to: addrs('t'),
    cc: addrs('c'),
    subject: m.su ?? '',
    draftId: m.id,
    draftHtml: html,
    origAttachments: attachmentsOf(m).map((a) => ({ messageId: m.id, part: a.part, filename: a.filename, size: a.size })),
    ...(origid && rt ? { inReplyTo: { origId: origid, replyType: rt } } : {}),
  };
}
/**
 * Build the compose seed for reply / reply-all / forward of `m`. `thread` is the
 * whole conversation `m` belongs to (when loaded): every attachment in it is
 * offered in the compose window as an include/exclude chip, referenced
 * server-side by message id + MIME part — never re-uploaded. Falls back to
 * just `m`'s own attachments.
 */
function buildDraft(m: Message, kind: 'reply' | 'replyAll' | 'forward', self: string, thread?: Message[]): ComposeInit {
  const from = fromAddr(m);
  const su = m.su ?? '';
  const when = fmtDayTime(m.d);
  const body = quotedOriginal(m);
  const source = thread && thread.some((t) => t.id === m.id) ? thread : [m];
  const origAttachments = [...source]
    .sort((a, b) => (a.d ?? 0) - (b.d ?? 0))
    .flatMap((t) =>
      attachmentsOf(t).map((a) => ({
        messageId: t.id,
        part: a.part,
        filename: a.filename,
        size: a.size,
        from: senderOf(t),
        date: t.d,
      })),
    );
  if (kind === 'forward') {
    const toLine = (m.e ?? []).filter((e) => e.t === 't').map((e) => e.a).join(', ');
    const hdr = `---------- Forwarded message ----------<br>From: ${escapeBodyText(senderOf(m))}<br>Date: ${escapeBodyText(when)}<br>Subject: ${escapeBodyText(su)}<br>To: ${escapeBodyText(toLine)}`;
    return {
      title: 'Forward',
      to: '',
      subject: /^fwd:/i.test(su) ? su : `Fwd: ${su}`,
      quotedHtml: `<div style="color:#667;margin:8px 0 8px">${hdr}</div>${body}`,
      inReplyTo: { origId: m.id, replyType: 'w' },
      origAttachments,
    };
  }
  const to = from?.a ?? '';
  let cc = '';
  if (kind === 'replyAll') {
    cc = (m.e ?? [])
      .filter((e) => (e.t === 't' || e.t === 'c') && e.a && e.a !== self && e.a !== to)
      .map((e) => e.a as string)
      .join(', ');
  }
  return {
    title: kind === 'replyAll' ? 'Reply all' : 'Reply',
    to,
    cc,
    subject: /^re:/i.test(su) ? su : `Re: ${su}`,
    quotedHtml: `<div style="color:#667;margin:8px 0 8px">On ${escapeBodyText(when)}, ${escapeBodyText(senderOf(m))} wrote:</div>${body}`,
    inReplyTo: { origId: m.id, replyType: 'r' },
    origAttachments,
  };
}

/** Calendar invite embedded in a message (runtime `m.inv[0].comp[0]`). */
interface InviteEvent {
  name?: string;
  loc?: string;
  compNum?: number;
  apptId?: string | number;
  s?: Array<{ u?: number }>;
  e?: Array<{ u?: number }>;
}
function inviteOf(m: Message | undefined): InviteEvent | undefined {
  const inv = (m as unknown as { inv?: Array<{ comp?: InviteEvent[] }> } | undefined)?.inv;
  return inv?.[0]?.comp?.[0];
}
function eventWhen(comp: InviteEvent): string {
  const s = comp.s?.[0]?.u;
  const e = comp.e?.[0]?.u;
  if (typeof s !== 'number') return '';
  const day = new Date(s).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const t = (ms: number) => new Date(ms).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', hour12: false });
  return typeof e === 'number' ? `${day} · ${t(s)} – ${t(e)}` : `${day} · ${t(s)}`;
}
/** "Add to calendar" card shown in place of an .ics attachment. */
function EventInviteCard({ msg, comp }: { msg: Message; comp: InviteEvent }) {
  const { client } = useClient();
  const exists = !!comp.apptId && String(comp.apptId) !== '0' && String(comp.apptId) !== '-1';
  const [state, setState] = useState<'added' | 'idle' | 'busy'>(exists ? 'added' : 'idle');
  async function add() {
    setState('busy');
    try {
      await client.call(JSNS.mail, 'SendInviteReply', {
        id: msg.id,
        compNum: comp.compNum ?? 0,
        verb: 'ACCEPT',
        updateOrganizer: 'FALSE', // add to my calendar without emailing the organizer
      });
      setState('added');
    } catch {
      setState('idle');
    }
  }
  return (
    <div className="event-card">
      <span className="event-cal-icon">
        <IconCalendar />
      </span>
      <div className="event-info">
        <div className="event-title">{comp.name || '(calendar event)'}</div>
        <div className="event-when">
          {eventWhen(comp)}
          {comp.loc ? ` · ${comp.loc}` : ''}
        </div>
      </div>
      <button className={`event-add${state === 'added' ? ' added' : ''}`} disabled={state !== 'idle'} onClick={add}>
        {state === 'added' ? '✓ Added' : state === 'busy' ? 'Adding…' : '+ Add to calendar'}
      </button>
    </div>
  );
}

/** First .ics / text/calendar part of a message (same test as attachmentsOf). */
function calPartOf(m: Message): string | undefined {
  const visit = (mp: MimePart[] | undefined): string | undefined => {
    for (const p of mp ?? []) {
      const isCal = p.ct === 'text/calendar' || (p.ct ?? '').startsWith('application/ics') || /\.ics$/i.test(p.filename ?? '');
      if (isCal && p.part) return p.part;
      const sub = visit(p.mp);
      if (sub) return sub;
    }
    return undefined;
  };
  return visit(m.mp);
}
interface IcsEvent {
  summary?: string;
  location?: string;
  start?: number;
  end?: number;
  allDay: boolean;
}
/** ICS date/date-time → epoch ms (UTC when suffixed Z, otherwise local wall time). */
function icsTime(v: string): { ms: number; allDay: boolean } | undefined {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?(Z)?)?$/.exec(v.trim());
  if (!m) return undefined;
  const [, y, mo, d, h, mi, se, z] = m;
  if (h === undefined) return { ms: new Date(+y!, +mo! - 1, +d!).getTime(), allDay: true };
  const args = [+y!, +mo! - 1, +d!, +h, +mi!, +(se ?? 0)] as const;
  return { ms: z ? Date.UTC(...args) : new Date(...args).getTime(), allDay: false };
}
/** Minimal parse of the first VEVENT in an iCalendar text (display only). */
function parseIcs(text: string): IcsEvent | undefined {
  const lines = text.replace(/\r?\n[ \t]/g, '').split(/\r?\n/);
  const unesc = (v: string) => v.replace(/\\n/gi, ' ').replace(/\\([,;\\])/g, '$1');
  let inEvent = false;
  let found = false;
  const ev: IcsEvent = { allDay: false };
  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') {
      inEvent = true;
      found = true;
      continue;
    }
    if (line === 'END:VEVENT') break;
    if (!inEvent) continue;
    const i = line.indexOf(':');
    if (i < 0) continue;
    const key = line.slice(0, i).split(';')[0]!.toUpperCase();
    const val = line.slice(i + 1);
    if (key === 'SUMMARY') ev.summary = unesc(val);
    else if (key === 'LOCATION') ev.location = unesc(val);
    else if (key === 'DTSTART') {
      const t = icsTime(val);
      if (t) {
        ev.start = t.ms;
        ev.allDay = t.allDay;
      }
    } else if (key === 'DTEND') ev.end = icsTime(val)?.ms;
  }
  return found ? ev : undefined;
}
/**
 * "Add to calendar" card for an .ics the server did not parse as an invite
 * (no `m.inv` — e.g. METHOD:PUBLISH, forwarded invites, plain .ics files).
 * Imports the part into the default calendar via ImportAppointments.
 */
function IcsInviteCard({ msg, part }: { msg: Message; part: string }) {
  const { client } = useClient();
  const qc = useQueryClient();
  const icsQ = useQuery({
    queryKey: ['ics-part', msg.id, part],
    queryFn: async () => {
      const r = await fetch(`/api/content?id=${encodeURIComponent(msg.id)}&part=${encodeURIComponent(part)}&disp=i`);
      if (!r.ok) throw new Error(`ics ${r.status}`);
      return parseIcs(await r.text()) ?? null;
    },
    staleTime: Infinity,
  });
  const [state, setState] = useState<'added' | 'idle' | 'busy' | 'error'>('idle');
  const ev = icsQ.data;
  if (!ev) return null;
  async function add() {
    setState('busy');
    try {
      await client.calendar.importAppointments({
        contentType: 'text/calendar',
        folderId: '10', // default Calendar
        messageId: msg.id,
        messagePart: part,
      });
      setState('added');
      void qc.invalidateQueries({ queryKey: ['calendar-appts'] });
    } catch {
      setState('error');
    }
  }
  let when = '';
  if (ev.start !== undefined) {
    when = ev.allDay
      ? new Date(ev.start).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
      : eventWhen({ s: [{ u: ev.start }], e: ev.end !== undefined ? [{ u: ev.end }] : undefined });
  }
  return (
    <div className="event-card">
      <span className="event-cal-icon">
        <IconCalendar />
      </span>
      <div className="event-info">
        <div className="event-title">{ev.summary || '(calendar event)'}</div>
        <div className="event-when">
          {when}
          {ev.location ? `${when ? ' · ' : ''}${ev.location}` : ''}
        </div>
      </div>
      <button className={`event-add${state === 'added' ? ' added' : ''}`} disabled={state === 'added' || state === 'busy'} onClick={add}>
        {state === 'added' ? '✓ Added' : state === 'busy' ? 'Adding…' : state === 'error' ? 'Failed — retry' : '+ Add to calendar'}
      </button>
    </div>
  );
}

interface MenuItem {
  label: string;
  danger?: boolean;
  run: () => void;
}
interface Menu {
  x: number;
  y: number;
  kind: 'conv' | 'msg';
  conv?: Conversation;
  msg?: Message;
}
/** Right-click context menu (closes on outside click / scroll / Escape). */
function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: Array<MenuItem | 'sep'>; onClose: () => void }) {
  useEffect(() => {
    const close = () => onClose();
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  const left = Math.min(x, window.innerWidth - 230);
  const top = Math.min(y, window.innerHeight - (items.length * 34 + 16));
  return (
    <div
      className="ctxmenu"
      style={{ left, top }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {items.map((it, i) =>
        it === 'sep' ? (
          <div key={`s${i}`} className="ctx-sep" />
        ) : (
          <button
            key={it.label}
            className={`ctx-item${it.danger ? ' danger' : ''}`}
            onClick={() => {
              it.run();
              onClose();
            }}
          >
            {it.label}
          </button>
        ),
      )}
    </div>
  );
}

/**
 * iOS-style swipe actions on a list row (mobile only): swipe RIGHT → Reply,
 * swipe LEFT → Delete; a full swipe fires the action instantly, a short swipe
 * snaps the coloured button open to tap. Desktop renders children unchanged.
 * Uses transform only (GPU-cheap) + `touch-action: pan-y` so vertical scroll
 * stays native and horizontal swipes are ours.
 */
function SwipeRow({
  enabled,
  onReply,
  onDelete,
  children,
}: {
  enabled: boolean;
  onReply: () => void;
  onDelete: () => void;
  children: ReactNode;
}) {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const st = useRef<{ x: number; y: number; dir: 0 | 1 | 2 }>({ x: 0, y: 0, dir: 0 });
  if (!enabled) return <>{children}</>;
  const OPEN = 72;
  const FULL = 155;
  const start = (e: React.TouchEvent) => {
    const t = e.touches[0];
    st.current = { x: t?.clientX ?? 0, y: t?.clientY ?? 0, dir: 0 };
    setAnimating(false);
  };
  const move = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t || document.body.classList.contains(TOUCH_DRAGGING)) return;
    const ddx = t.clientX - st.current.x;
    const ddy = t.clientY - st.current.y;
    if (st.current.dir === 0 && (Math.abs(ddx) > 8 || Math.abs(ddy) > 8)) {
      st.current.dir = Math.abs(ddx) > Math.abs(ddy) ? 1 : 2;
    }
    if (st.current.dir === 1) setDx(Math.max(-260, Math.min(260, ddx)));
  };
  const end = () => {
    setAnimating(true);
    st.current.dir = 0;
    if (dx > FULL) {
      setDx(0);
      onReply();
    } else if (dx < -FULL) {
      setDx(-(typeof window !== 'undefined' ? window.innerWidth : 400));
      window.setTimeout(onDelete, 170);
    } else if (Math.abs(dx) > OPEN) {
      setDx(dx > 0 ? OPEN : -OPEN);
    } else {
      setDx(0);
    }
  };
  return (
    <div className="swipe-wrap">
      <button
        type="button"
        className="swipe-act reply"
        style={{ opacity: dx > 6 ? 1 : 0, width: Math.max(72, dx) }}
        onClick={() => {
          setAnimating(true);
          setDx(0);
          onReply();
        }}
        tabIndex={-1}
        aria-label="Reply"
      >
        <IconReply />
      </button>
      <button
        type="button"
        className="swipe-act delete"
        style={{ opacity: dx < -6 ? 1 : 0, width: Math.max(72, -dx) }}
        onClick={() => onDelete()}
        tabIndex={-1}
        aria-label="Delete"
      >
        <IconTrash />
      </button>
      <div
        className="swipe-fg"
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? 'transform 0.18s ease' : 'none',
          background: dx !== 0 ? 'var(--bg)' : undefined,
        }}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        onClickCapture={(e) => {
          if (dx !== 0) {
            e.stopPropagation();
            setAnimating(true);
            setDx(0);
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function Mailbox({
  query,
  session,
  openMsgId,
  onSearch,
}: {
  query: string;
  session: string;
  openMsgId?: string | null;
  /** run a mail search (also fills the search box) — used by "Find emails" */
  onSearch?: (q: string) => void;
}) {
  const [activeFolder, setActiveFolder] = useState('2'); // Inbox
  // Clean inbox: rule from a mail — drag with a mouse, press-and-hold with a finger, or the broom button / row menu
  const cleanFromMail = useCleanRuleFromMail();
  const mouseDrag = canMouseDrag();
  const [sortBy, setSortBy] = useState('dateDesc');
  const [openConvId, setOpenConvId] = useState<string | null>(null); // conv whose thread is loaded
  const [treeConvId, setTreeConvId] = useState<string | null>(null); // conv whose thread is folded out
  const [selectedMsgId, setSelectedMsgId] = useState<string | null>(null);
  const [compose, setCompose] = useState<ComposeInit | null>(null);

  const searching = query.trim().length > 0;
  /** the folder on screen — a mail in a share reports the owner's folder id, so this is what a clean rule uses */
  const viewedFolder = searching ? undefined : activeFolder;

  const foldersQ = useFolders();
  const convsQ = useConversations(activeFolder, sortBy);
  // shared mountpoint ids so search also covers the user's shares (other accounts)
  const sharedIds = useMemo(
    () => buildRows(foldersQ.data).filter((f) => f.shared).map((f) => f.id),
    [foldersQ.data],
  );
  const searchQ = useSearch(query, sharedIds);
  const threadQ = useConversation(!searching ? openConvId : null);
  const singleQ = useMessage(selectedMsgId);
  const action = useMailAction();
  const { client, session: clientSession } = useClient();
  const sessionCsrf = clientSession.csrfToken;
  const qc = useQueryClient();
  const [menu, setMenu] = useState<Menu | null>(null);
  // right-click / click menu on an address in the reading-pane header
  const [addrMenu, setAddrMenu] = useState<{ x: number; y: number; addr: EmailAddr } | null>(null);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);
  const createContact = useCreateContact();

  const folders = useMemo(() => buildRows(foldersQ.data), [foldersQ.data]);
  const sortedThread = useMemo(() => sortMessages(threadQ.data ?? [], sortBy), [threadQ.data, sortBy]);

  // open a specific message when asked (e.g. an AI answer citation)
  useEffect(() => {
    if (openMsgId) setSelectedMsgId(openMsgId);
  }, [openMsgId]);

  // browser tab title reflects the inbox unread count
  const inboxUnread = useMemo(() => folders.find((f) => f.id === '2')?.unread ?? 0, [folders]);
  useEffect(() => {
    // unread as a tab "badge": (N) prefix — the cross-browser way (Firefox has
    // no app-badge API), so the tab shows e.g. "(3) Mo2 - Inbox".
    document.title = inboxUnread > 0 ? `(${inboxUnread}) Mo2 - Inbox` : 'Mo2 - Inbox';
  }, [inboxUnread]);

  // auto-open the latest message of a conversation when it's opened
  useEffect(() => {
    if (searching || !openConvId || sortedThread.length === 0) return;
    if (selectedMsgId && sortedThread.some((m) => m.id === selectedMsgId)) return;
    const latest = sortedThread.reduce((a, b) => ((b.d ?? 0) > (a.d ?? 0) ? b : a));
    setSelectedMsgId(latest.id);
  }, [sortedThread, openConvId, searching, selectedMsgId]);

  /** Fetch a saved draft and continue it in the compose window. */
  async function openDraft(id: string) {
    try {
      const r = await client.mail.getMessage({ id, html: true, neuter: false });
      const m = r.m?.[0];
      if (m) setCompose(draftInit(m));
    } catch {
      setNotice('Could not open the draft');
    }
  }
  function selectMsg(m: Message) {
    // a draft opens in the compose window, as it was left (ap 2026-10-29)
    if (isDraft(m)) {
      void openDraft(m.id);
      return;
    }
    setSelectedMsgId(m.id);
    if (msgUnread(m)) action.mutate({ kind: 'msg', ids: m.id, op: 'read' });
  }
  function openConv(c: Conversation) {
    // in Drafts a row is a draft: open it in the compose window, not the reading pane
    const draft = activeFolder === '6' && !searching ? (c.m ?? []).find((x) => x.l === '6' || isDraft(x)) : undefined;
    if (draft) {
      void openDraft(draft.id);
      return;
    }
    setOpenConvId(c.id);
    if (convUnread(c)) action.mutate({ kind: 'conv', ids: c.id, op: 'read' });
  }
  function toggleTree(c: Conversation, e: React.MouseEvent) {
    e.stopPropagation();
    setOpenConvId(c.id);
    setTreeConvId((prev) => (prev === c.id ? null : c.id));
    if (convUnread(c)) action.mutate({ kind: 'conv', ids: c.id, op: 'read' });
  }
  function doAction(op: string) {
    if (!selectedMsgId) return;
    action.mutate({ kind: 'msg', ids: selectedMsgId, op });
    // spam/!spam both move the message out of the current folder
    if (op === 'trash' || op === 'spam' || op === '!spam' || op === 'delete') setSelectedMsgId(null);
  }

  // --- right-click context menu (reply/forward/edit/etc. from the list) ---
  function openMenu(e: React.MouseEvent, kind: 'conv' | 'msg', item: Conversation | Message) {
    e.preventDefault();
    e.stopPropagation();
    setMenu({
      x: e.clientX,
      y: e.clientY,
      kind,
      conv: kind === 'conv' ? (item as Conversation) : undefined,
      msg: kind === 'msg' ? (item as Message) : undefined,
    });
  }
  /** Resolve the message to act on (a conv's latest message is fetched on demand),
   *  plus the conversation it belongs to when that was fetched. */
  async function targetMsg(m: Menu): Promise<{ msg: Message; thread?: Message[] } | undefined> {
    if (m.kind === 'msg') return m.msg ? { msg: m.msg } : undefined;
    if (!m.conv) return undefined;
    const res = await client.mail.getConversation({ id: m.conv.id, fetch: 'all', html: true });
    const msgs = (res.c?.[0]?.m ?? []) as Message[];
    if (!msgs.length) return undefined;
    return { msg: msgs.reduce((a, b) => ((b.d ?? 0) > (a.d ?? 0) ? b : a)), thread: msgs };
  }
  async function composeFor(m: Menu, kind: 'reply' | 'replyAll' | 'forward') {
    const t = await targetMsg(m);
    if (t) setCompose(buildDraft(t.msg, kind, session, t.thread));
  }
  async function editAsNew(m: Menu) {
    const t = await targetMsg(m);
    if (t) setCompose({ title: 'New Message', subject: t.msg.su ?? '', text: plainBody(t.msg) });
  }
  function menuAction(m: Menu, op: string) {
    action.mutate({ kind: m.kind, ids: (m.kind === 'conv' ? m.conv?.id : m.msg?.id) ?? '', op });
  }
  function openAddrMenu(e: React.MouseEvent, addr: EmailAddr) {
    e.preventDefault();
    e.stopPropagation(); // keep the window listener from closing it at once
    setMenu(null);
    setAddrMenu({ x: e.clientX, y: e.clientY, addr });
  }
  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setNotice('Copied');
    } catch {
      setNotice('Copy failed');
    }
  }
  /** the badge state the list already loaded for this sender (from the batched peek cache) */
  function cachedBadge(email: string): BadgeInfo | undefined {
    for (const [, data] of qc.getQueriesData<Record<string, BadgeInfo>>({ queryKey: ['badges'] }))
      if (data?.[email]) return data[email];
    return undefined;
  }
  async function badgeAction(kind: 'ban' | 'pin', email: string, pinned?: boolean) {
    try {
      const r = await fetch(`/api/badge/${kind}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'x-zwf-csrf': sessionCsrf },
        body: JSON.stringify(kind === 'pin' ? { email, pinned } : { email }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setNotice(kind === 'ban' ? 'Picture removed — it will not be shown again' : pinned ? 'Picture kept' : 'Picture unpinned');
      void qc.invalidateQueries({ queryKey: ['badges'] });
    } catch {
      setNotice('Could not change the picture');
    }
  }
  async function addToContacts(e: EmailAddr) {
    const addr = e.a ?? '';
    if (!addr) return;
    try {
      const res = await client.call<{ cn?: unknown }>(JSNS.mail, 'Search', {
        types: 'contact',
        query: `"${addr.replace(/"/g, '')}"`,
        limit: 1,
      });
      if (res.cn && (!Array.isArray(res.cn) || res.cn.length > 0)) {
        setNotice(`${addr} is already in your contacts`);
        return;
      }
      const name = (e.p ?? '').trim();
      const sp = name.indexOf(' ');
      const attrs = [{ n: 'email', _content: addr }];
      if (name && name !== addr) {
        attrs.push({ n: 'firstName', _content: sp > 0 ? name.slice(0, sp) : name });
        if (sp > 0) attrs.push({ n: 'lastName', _content: name.slice(sp + 1) });
      }
      await createContact.mutateAsync({ folderId: '7', attrs }); // 7 = Contacts
      setNotice(`Added ${name || addr} to contacts`);
    } catch {
      setNotice('Could not add contact');
    }
  }
  function addrMenuItems(e: EmailAddr): Array<MenuItem | 'sep'> {
    const addr = e.a ?? '';
    const full = addrFull(e);
    const items: Array<MenuItem | 'sep'> = [];
    if (addr) items.push({ label: 'Copy address', run: () => void copyText(addr) });
    if (full !== addr) items.push({ label: 'Copy name and address', run: () => void copyText(full) });
    items.push('sep', { label: 'New message', run: () => setCompose({ title: 'New Message', to: full }) });
    if (addr && onSearch) {
      items.push({ label: 'Find emails', run: () => onSearch(`from:"${addr}" OR to:"${addr}" OR cc:"${addr}"`) });
    }
    if (addr) items.push('sep', { label: 'Add to contacts', run: () => void addToContacts(e) });
    // badge operator actions (plan v3 §7) — only when this sender currently has a picture
    const badge = cachedBadge(addr.toLowerCase());
    if (badge?.ok) {
      items.push('sep', { label: 'Wrong picture', danger: true, run: () => void badgeAction('ban', addr) });
      items.push(
        badge.pinned
          ? { label: 'Unpin picture', run: () => void badgeAction('pin', addr, false) }
          : { label: 'Keep this picture', run: () => void badgeAction('pin', addr, true) },
      );
    }
    return items;
  }
  /** one clickable address in the reading-pane header (click or right-click → menu) */
  function addrChip(e: EmailAddr, i: number, label?: string) {
    return (
      <Fragment key={`${e.a ?? ''}-${i}`}>
        {i > 0 ? ', ' : ''}
        <span
          className="addr-chip"
          title={addrFull(e)}
          onClick={(ev) => openAddrMenu(ev, e)}
          onContextMenu={(ev) => openAddrMenu(ev, e)}
        >
          {label ?? addrFull(e)}
        </span>
      </Fragment>
    );
  }

  function menuItems(m: Menu): Array<MenuItem | 'sep'> {
    const unread = m.kind === 'conv' ? convUnread(m.conv as Conversation) : msgUnread(m.msg as Message);
    return [
      { label: 'Open', run: () => (m.kind === 'conv' ? openConv(m.conv as Conversation) : selectMsg(m.msg as Message)) },
      { label: 'Reply', run: () => void composeFor(m, 'reply') },
      { label: 'Reply all', run: () => void composeFor(m, 'replyAll') },
      { label: 'Forward', run: () => void composeFor(m, 'forward') },
      { label: 'Edit as new', run: () => void editAsNew(m) },
      'sep',
      { label: unread ? 'Mark as read' : 'Mark as unread', run: () => menuAction(m, unread ? 'read' : '!read') },
      { label: 'Flag', run: () => menuAction(m, 'flag') },
      ...(cleanFromMail
        ? [
            {
              label: 'Clean rule for this sender…',
              run: () => {
                const d = m.kind === 'conv' ? mailOfConv(m.conv as Conversation, viewedFolder) : mailOfMsg(m.msg as Message, viewedFolder);
                if (d) cleanFromMail(d);
              },
            },
          ]
        : []),
      m.kind === 'msg' && m.msg?.l === JUNK_FOLDER_ID
        ? { label: 'Not junk', run: () => menuAction(m, '!spam') }
        : { label: 'Mark as junk', run: () => menuAction(m, 'spam') },
      'sep',
      { label: 'Delete', danger: true, run: () => menuAction(m, 'trash') },
    ];
  }

  const single = singleQ.data;
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // per-message override for loading remote images (the global default + a
  // one-tap "Load images" button); reset whenever a different message opens.
  const [imgOverride, setImgOverride] = useState(false);
  useEffect(() => setImgOverride(false), [selectedMsgId]);
  const loadImages = getLoadExternalImages() || imgOverride;
  // mobile: horizontal swipe on the reading pane goes back to the list
  const backSwipe = useRef({ x: 0, y: 0 });
  const goBackToList = () => {
    setSelectedMsgId(null);
    setOpenConvId(null);
    setTreeConvId(null);
  };
  const body = useMemo(() => extractBody(single?.mp), [single]);
  const conversations = useMemo(() => convsQ.data?.pages.flatMap((p) => p.items) ?? [], [convsQ.data]);
  const threadPeopleMap = useThreadPeople(conversations, session);
  const searchList = useMemo(() => searchQ.data?.pages.flatMap((p) => p.items) ?? [], [searchQ.data]);
  const activeQ = searching ? searchQ : convsQ;

  // infinite scroll + floating scroll-date indicator
  const scrollRef = useRef<HTMLDivElement>(null);
  const [floatDate, setFloatDate] = useState('');
  const [floatShow, setFloatShow] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  function onListScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (activeQ.hasNextPage && !activeQ.isFetchingNextPage && el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
      void activeQ.fetchNextPage();
    }
    const top = el.getBoundingClientRect().top + 6;
    let d = '';
    for (const r of el.querySelectorAll<HTMLElement>('[data-date]')) {
      if (r.getBoundingClientRect().bottom > top) {
        d = r.dataset.date ?? '';
        break;
      }
    }
    if (d) setFloatDate(d);
    setFloatShow(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setFloatShow(false), 1100);
  }

  function messageRow(m: Message, child: boolean) {
    const who = senderOf(m);
    return (
      <SwipeRow
        key={m.id}
        enabled={isMobile}
        onReply={() => void composeFor({ kind: 'msg', msg: m } as Menu, 'reply')}
        onDelete={() => menuAction({ kind: 'msg', msg: m } as Menu, 'trash')}
      >
        <div
          className={`row${child ? ' tree-msg child' : ''}${msgUnread(m) ? ' unread' : ''}${selectedMsgId === m.id ? ' active' : ''}`}
          data-date={floatLabel(m.d)}
          style={child ? { paddingLeft: 52 } : undefined}
          draggable={mouseDrag}
          onDragStart={(ev) => startMailDrag(ev, mailOfMsg(m, viewedFolder))}
          onTouchStart={(ev) => touchDragStart(ev, () => mailOfMsg(m, viewedFolder), who)}
          onClick={() => selectMsg(m)}
          onContextMenu={(e) => openMenu(e, 'msg', m)}
        >
          {msgUnread(m) ? (
            <span className="unread-mark">
              <IconMail />
            </span>
          ) : null}
          {msgHasAttach(m) ? (
            <span className="attach-mark" title="Has attachment">
              <IconPaperclip />
            </span>
          ) : null}
          <Avatar name={who} email={msgEmail(m)} />
          <div className="row-body">
            <div className="top">
              <span className="from">{who}</span>
              <span className="date">{fmtDate(m.d)}</span>
            </div>
            <div className="subj">{m.su || '(no subject)'}</div>
            <div className="frag">{m.fr ?? ''}</div>
          </div>
        </div>
      </SwipeRow>
    );
  }

  return (
    <BadgeProvider>
    <div
      className={`shell${isMobile ? ' m-shell' : ''}${drawerOpen ? ' m-drawer' : ''}${
        isMobile && selectedMsgId ? ' m-detail' : ''
      }`}
    >
      {isMobile && drawerOpen ? (
        <div className="m-backdrop" onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside className="pane sidebar m-side">
        <div className="sidebar-top">
          <button className="primary compose-btn" onClick={() => setCompose({ title: 'New Message' })}>
            ✎ Compose
          </button>
        </div>
        <div className="pane-hdr">Mailboxes</div>
        <div className="pane-scroll">
          {folders.map((f) => (
            <div
              key={f.id}
              className={`folder${f.id === activeFolder && !searching ? ' active' : ''}${f.id === '2' ? ' inbox' : ''}`}
              style={{ paddingLeft: 12 + f.depth * 14 }}
              onClick={() => {
                setActiveFolder(f.id);
                setOpenConvId(null);
                setTreeConvId(null);
                setSelectedMsgId(null);
                setDrawerOpen(false);
              }}
            >
              <span className="fname">
                {f.shared ? <IconShare className="share-glyph" /> : null}
                {f.name}
              </span>
              {f.unread ? <span className="count">{f.unread}</span> : null}
            </div>
          ))}
        </div>
      </aside>

      <section className="pane list m-list-pane">
        <div className="pane-hdr listhdr">
          <button className="m-burger" aria-label="Folders" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
          <span>
            {searching
              ? `Search: ${query}`
              : convsQ.isFetching && !convsQ.isFetchingNextPage
                ? 'Refreshing…'
                : `${conversations.length} items`}
          </span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className={`list-float-date${floatShow && floatDate ? ' show' : ''}`}>{floatDate}</div>
        <div className="pane-scroll" ref={scrollRef} onScroll={onListScroll}>
          {searching ? (
            searchQ.isLoading ? (
              <div className="loading">Loading…</div>
            ) : searchList.length === 0 ? (
              <div className="loading">No results</div>
            ) : (
              searchList.map((m) => messageRow(m, false))
            )
          ) : convsQ.isLoading ? (
            <div className="loading">Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="loading">No conversations</div>
          ) : (
            conversations.map((c) => {
              const tp = threadPeopleMap.get(c.id);
              const who = tp ? tp.starter.name : participantsOf(c);
              const count = c.n ?? 1;
              const hasThread = count > 1;
              const treeOpen = treeConvId === c.id;
              return (
                <Fragment key={c.id}>
                  <SwipeRow
                    enabled={isMobile}
                    onReply={() => void composeFor({ kind: 'conv', conv: c } as Menu, 'reply')}
                    onDelete={() => menuAction({ kind: 'conv', conv: c } as Menu, 'trash')}
                  >
                  <div
                    className={`row conv-row${convUnread(c) ? ' unread' : ''}${openConvId === c.id ? ' active' : ''}`}
                    data-date={floatLabel(c.d)}
                    draggable={mouseDrag}
                    onDragStart={(ev) => startMailDrag(ev, mailOfConv(c, viewedFolder))}
                    onTouchStart={(ev) => touchDragStart(ev, () => mailOfConv(c, viewedFolder), who)}
                    onClick={() => openConv(c)}
                    onContextMenu={(e) => openMenu(e, 'conv', c)}
                  >
                    {convUnread(c) ? (
                      <span className="unread-mark">
                        <IconMail />
                      </span>
                    ) : null}
                    {convHasAttach(c) ? (
                      <span className="attach-mark" title="Has attachment">
                        <IconPaperclip />
                      </span>
                    ) : null}
                    {tp ? (
                      <div className="av-slot thread-orbit">
                        <Avatar name={tp.starter.name} email={validEmail(tp.starter.email)} />
                        <div className="thread-stack">
                          {tp.others.map((p) => (
                            <Avatar key={p.email} mini name={p.name} email={validEmail(p.email)} />
                          ))}
                          {tp.more > 0 ? (
                            <div className="avatar mini more" title={`${tp.more} more`}>
                              +{tp.more}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <div className="av-slot">
                        <Avatar name={who} email={convEmail(c)} />
                      </div>
                    )}
                    <div className="row-body">
                      <div className="top">
                        <span className="from">{who}</span>
                        <span className="date">{fmtDate(c.d)}</span>
                      </div>
                      <div className="subj">
                        {c.su || '(no subject)'}
                        {hasThread ? <span className="thread-count">{count}</span> : null}
                      </div>
                      <div className="frag">{c.fr ?? ''}</div>
                    </div>
                    {hasThread ? (
                      <span
                        className={`dots-badge${treeOpen ? ' open' : ''}`}
                        onClick={(e) => toggleTree(c, e)}
                        title={treeOpen ? 'Collapse thread' : 'Expand thread'}
                        aria-label="Toggle thread"
                      >
                        ⋯
                      </span>
                    ) : null}
                  </div>
                  </SwipeRow>
                  {treeOpen
                    ? threadQ.isLoading
                      ? <div className="loading">Loading thread…</div>
                      : sortedThread.map((m) => messageRow(m, true))
                    : null}
                </Fragment>
              );
            })
          )}
          {activeQ.isFetchingNextPage ? <div className="loading">Loading more…</div> : null}
        </div>
      </section>

      <main
        className="pane reading m-detail-pane"
        onTouchStart={
          isMobile
            ? (e) => {
                const t = e.touches[0];
                backSwipe.current = { x: t?.clientX ?? 0, y: t?.clientY ?? 0 };
              }
            : undefined
        }
        onTouchEnd={
          isMobile
            ? (e) => {
                const t = e.changedTouches[0];
                if (!t) return;
                const dxe = t.clientX - backSwipe.current.x;
                const dye = t.clientY - backSwipe.current.y;
                // a clear horizontal swipe (either direction) returns to the list
                if (Math.abs(dxe) > 80 && Math.abs(dxe) > Math.abs(dye) * 1.5) goBackToList();
              }
            : undefined
        }
      >
        {!selectedMsgId ? (
          <div className="empty">Select a message</div>
        ) : singleQ.isLoading ? (
          <div className="loading">Loading…</div>
        ) : single ? (
          <>
            <div className="reading-mbar">
              <button
                className="m-back"
                aria-label="Back to list"
                onClick={() => {
                  // clear the open conversation too, else the auto-open-latest
                  // effect re-selects the message and snaps back to the reading view
                  setSelectedMsgId(null);
                  setOpenConvId(null);
                  setTreeConvId(null);
                }}
              >
                ‹ Inbox
              </button>
            </div>
            <div className="reading-hdr">
              <Avatar name={senderOf(single)} email={msgEmail(single)} large />
              <div className="reading-hdr-main">
                <h2>{single.su || '(no subject)'}</h2>
                <div className="meta">
                  {(() => {
                    const a = fromAddr(single);
                    const name = senderOf(single);
                    return (
                      <>
                        {a ? (
                          <span
                            className="addr-chip"
                            title={addrFull(a)}
                            onClick={(ev) => openAddrMenu(ev, a)}
                            onContextMenu={(ev) => openAddrMenu(ev, a)}
                          >
                            <span className="from-name">{name}</span>
                            {a.a && a.a !== name ? <span className="from-addr">{` <${a.a}>`}</span> : null}
                          </span>
                        ) : (
                          <span className="from-name">{name}</span>
                        )}
                        {single.d ? ` · ${fmtDayTime(single.d)}` : ''}
                      </>
                    );
                  })()}
                </div>
                {(() => {
                  const to = addrObjsByType(single, 't');
                  const cc = addrObjsByType(single, 'c');
                  if (!to.length && !cc.length) return null;
                  return (
                    <div className="reading-recipients">
                      {to.length ? (
                        <div className="rcpt-line">
                          <span className="rcpt-label">To</span>
                          <span className="rcpt-names">{to.map((e, i) => addrChip(e, i))}</span>
                        </div>
                      ) : null}
                      {cc.length ? (
                        <div className="rcpt-line">
                          <span className="rcpt-label">Cc</span>
                          <span className="rcpt-names">{cc.map((e, i) => addrChip(e, i))}</span>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                <div className="reading-actions">
                  {(() => {
                    const sc = parseSpamScore(single);
                    return sc !== null ? <SpamMeter score={sc} /> : null;
                  })()}
                  <div className="reading-toolbar">
                <button
                  className={`load-img-btn${loadImages ? ' active' : ''}`}
                  title={loadImages ? 'Remote images loaded' : 'Load remote images'}
                  aria-label="Load images"
                  onClick={() => setImgOverride(true)}
                >
                  <IconImage />
                </button>
                <button title="Reply" aria-label="Reply" onClick={() => setCompose(buildDraft(single, 'reply', session, sortedThread))}>
                  <IconReply />
                </button>
                <button title="Reply all" aria-label="Reply all" onClick={() => setCompose(buildDraft(single, 'replyAll', session, sortedThread))}>
                  <IconReplyAll />
                </button>
                <button title="Forward" aria-label="Forward" onClick={() => setCompose(buildDraft(single, 'forward', session, sortedThread))}>
                  <IconForward />
                </button>
                <span className="tb-sep" />
                <button title="Flag" aria-label="Flag" onClick={() => doAction('flag')}>
                  <IconFlag />
                </button>
                <button title="Mark unread" aria-label="Mark unread" onClick={() => doAction('!read')}>
                  <IconMail />
                </button>
                {cleanFromMail ? (
                  <button
                    title="Clean rule for this sender"
                    aria-label="Clean rule for this sender"
                    onClick={() => {
                      const d = mailOfMsg(single, viewedFolder);
                      if (d) cleanFromMail(d);
                    }}
                  >
                    <IconBroom />
                  </button>
                ) : null}
                {single.l === JUNK_FOLDER_ID ? (
                  <button title="Not junk" aria-label="Not junk" onClick={() => doAction('!spam')}>
                    <IconJunk />
                  </button>
                ) : (
                  <button title="Junk" aria-label="Junk" onClick={() => doAction('spam')}>
                    <IconJunk />
                  </button>
                )}
                <button className="danger" title="Delete" aria-label="Delete" onClick={() => doAction('trash')}>
                  <IconTrash />
                </button>
                  </div>
                  {(() => {
                    const atts = attachmentsOf(single);
                    if (atts.length === 0) return null;
                    return (
                      <div className="att-group">
                        <span className="att-group-clip" aria-label="Attachments" title="Attachments">
                          <IconPaperclip />
                        </span>
                        <div className="att-badges">
                          {atts.map((a) => {
                            const disp = attachInline(a.ct) ? 'i' : 'a';
                            const t = attType(a);
                            const Glyph = attIcon(a);
                            return (
                              <a
                                key={a.part}
                                className="att-item"
                                title={`${a.filename}${a.size ? ` · ${fmtSize(a.size)}` : ''}`}
                                href={`/api/content?id=${encodeURIComponent(single.id)}&part=${encodeURIComponent(a.part)}&disp=${disp}`}
                                target="_blank"
                                rel="noreferrer"
                                {...(disp === 'a' ? { download: a.filename } : {})}
                              >
                                <span className="att-badge" style={{ background: attColor(t) }}>
                                  <Glyph />
                                </span>
                                <span className="att-name">{a.filename}</span>
                              </a>
                            );
                          })}
                          {atts.length > 1 ? (
                            <a
                              className="att-item"
                              title="Download all attachments as a zip"
                              href={`/api/content?id=${encodeURIComponent(single.id)}&fmt=zip&disp=a`}
                              target="_blank"
                              rel="noreferrer"
                              download
                            >
                              <span className="att-badge att-all">
                                <span className="att-type">ALL</span>
                              </span>
                              <span className="att-name">All · zip</span>
                            </a>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="reading-body">
              {(() => {
                const comp = inviteOf(single);
                if (comp) return <EventInviteCard msg={single} comp={comp} />;
                const calPart = calPartOf(single);
                return calPart ? <IcsInviteCard key={`${single.id}-${calPart}`} msg={single} part={calPart} /> : null;
              })()}
              {(() => {
                const bodyHtml = messageBodyHtml(single, body, loadImages);
                return bodyHtml ? (
                  <iframe
                    // allow-same-origin (NOT allow-scripts) so inline cid: images
                    // load from same-origin /api/content with the session cookie;
                    // email JS still cannot run.
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    title="message"
                    srcDoc={bodyHtml}
                  />
                ) : (
                  <div className="text">{body.text ?? '(no content)'}</div>
                );
              })()}
            </div>
          </>
        ) : null}
      </main>

      {compose ? <ComposeWindow init={compose} onClose={() => setCompose(null)} /> : null}
      {menu ? <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu)} onClose={() => setMenu(null)} /> : null}
      {addrMenu ? (
        <ContextMenu x={addrMenu.x} y={addrMenu.y} items={addrMenuItems(addrMenu.addr)} onClose={() => setAddrMenu(null)} />
      ) : null}
      {notice ? <div className="mini-toast">{notice}</div> : null}
    </div>
    </BadgeProvider>
  );
}
